import React, { useState, useRef, useMemo } from 'react';
import {
  X, Image as ImageIcon, Video, FileText, BarChart2,
  Calendar as CalendarIcon, Sparkles, Globe, Users,
  Plus, Trash2, Loader2, ChevronDown, Smile, Paperclip,
} from 'lucide-react';
import { admin, ai } from '../../../lib/api';
import { useNotify } from '../../../components/NotifyProvider';
import { getIntlLocale } from '../../../i18n/runtime';

const ALLOWED_IMG = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const ALLOWED_VID = ['video/mp4', 'video/quicktime', 'video/webm'];
const ALLOWED_DOC = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
const MAX_IMG = 10 * 1024 * 1024;
const MAX_VID = 50 * 1024 * 1024;
const MAX_DOC = 100 * 1024 * 1024;
const POLL_DURATIONS = [
  { d: 1, label: '1 day' },
  { d: 3, label: '3 days' },
  { d: 7, label: '1 week' },
  { d: 14, label: '2 weeks' },
];
const EMOJIS = ['👍','🎉','🙏','🙌','🔥','💡','✅','❤️','😊','😂','🤝','🚀','👏','⭐','💪','💼','📩','🗓','👀','🧠','📈','🙋‍♂️','😉','👋'];

function toLocalInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * LinkedIn-parity composer for Admin → Client posts.
 *
 * Modes (post_type):
 *   text      — plain body
 *   photo     — body + image (≤10 MB jpeg/png/webp/gif)
 *   video     — body + video (≤50 MB mp4/mov/webm)
 *   article   — title + body (+ optional URL)
 *   poll      — body (question) + 2-4 options + duration
 *
 * Submission goes to /api/admin/users/:id/posts/draft → status=pending.
 * The client must approve in /app/approvals (or via email link) before
 * Unipile actually publishes the post on LinkedIn.
 */
export default function AdminPostComposer({ target, onClose, onSent, initialDraft = null }) {
  const notify = useNotify();
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const docRef = useRef(null);
  const textRef = useRef(null);

  // Close on Escape only (not backdrop) — prevents accidental loss of a
  // composed draft if the user clicks outside the modal.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isRevision = !!initialDraft?.id;
  const [postType, setPostType] = useState(initialDraft?.post_type || 'text');
  const [visibility, setVisibility] = useState(initialDraft?.visibility || 'anyone');
  const [text, setText] = useState(initialDraft?.text || '');
  const [linkUrl, setLinkUrl] = useState(initialDraft?.link_url || '');
  const [linkOpen, setLinkOpen] = useState(!!initialDraft?.link_url);
  const [articleTitle, setArticleTitle] = useState(initialDraft?.article_title || '');
  const [pollOptions, setPollOptions] = useState(initialDraft?.poll_options?.length ? initialDraft.poll_options : ['', '']);
  const [pollDays, setPollDays] = useState(initialDraft?.poll_duration_days || 7);
  const [scheduledAt, setScheduledAt] = useState(toLocalInputValue(initialDraft?.scheduled_at));
  const [scheduleOpen, setScheduleOpen] = useState(!!initialDraft?.scheduled_at);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [visOpen, setVisOpen] = useState(false);

  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [clearExistingMedia, setClearExistingMedia] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [rewriting, setRewriting] = useState(false);

  const charCount = text.length;
  const hasExistingMedia = !clearExistingMedia && (
    (postType === 'photo' && initialDraft?.has_image) ||
    (postType === 'video' && initialDraft?.has_video) ||
    (postType === 'document' && initialDraft?.has_document)
  );
  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (postType === 'poll') return text.trim() && pollOptions.filter((o) => o.trim()).length >= 2;
    if (postType === 'photo') return !!file || hasExistingMedia;
    if (postType === 'video') return !!videoFile || hasExistingMedia;
    if (postType === 'document') return !!docFile || hasExistingMedia;
    if (postType === 'article') return text.trim() && articleTitle.trim();
    return text.trim();
  }, [postType, text, file, videoFile, docFile, articleTitle, pollOptions, submitting, hasExistingMedia]);

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_IMG.includes(f.type)) {
      notify.error('Unsupported image', 'Please pick a JPEG / PNG / WebP / GIF.');
      return;
    }
    if (f.size > MAX_IMG) { notify.error('Image too large', 'Max file size is 10 MB.'); return; }
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
    setClearExistingMedia(false);
    setPostType('photo');
  };
  const onPickVideo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_VID.includes(f.type)) {
      notify.error('Unsupported video', 'Please pick an MP4 / MOV / WebM.');
      return;
    }
    if (f.size > MAX_VID) { notify.error('Video too large', 'Max file size is 50 MB.'); return; }
    setVideoFile(f);
    setVideoPreview(URL.createObjectURL(f));
    setClearExistingMedia(false);
    setPostType('video');
  };
  const removeImage = () => {
    setFile(null);
    setFilePreview(null);
    if (initialDraft?.has_image) setClearExistingMedia(true);
    if (fileRef.current) fileRef.current.value = '';
    if (postType === 'photo') setPostType('text');
  };
  const removeVideo = () => {
    setVideoFile(null);
    setVideoPreview(null);
    if (initialDraft?.has_video) setClearExistingMedia(true);
    if (videoRef.current) videoRef.current.value = '';
    if (postType === 'video') setPostType('text');
  };
  const onPickDoc = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_DOC.includes(f.type) && !/\.(pdf|docx?|pptx?)$/i.test(f.name)) {
      notify.error('Unsupported document', 'Use PDF / DOC / DOCX / PPT / PPTX.');
      return;
    }
    if (f.size > MAX_DOC) { notify.error('Document too large', 'Max file size is 100 MB.'); return; }
    setDocFile(f);
    setClearExistingMedia(false);
    setPostType('document');
  };
  const removeDoc = () => {
    setDocFile(null);
    if (initialDraft?.has_document) setClearExistingMedia(true);
    if (docRef.current) docRef.current.value = '';
    if (postType === 'document') setPostType('text');
  };

  const insertEmoji = (e) => {
    setText((p) => p + e);
    setEmojiOpen(false);
    setTimeout(() => textRef.current?.focus(), 0);
  };

  const handleRewrite = async () => {
    if (!text.trim() || rewriting) return;
    try {
      setRewriting(true);
      const { rewritten } = await ai.rewrite(text, 'professional');
      if (rewritten) setText(rewritten);
    } catch (err) {
      notify.error('Rewrite failed', err.message || 'Please try again.');
    } finally {
      setRewriting(false);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    if (linkUrl && !/^https?:\/\//i.test(linkUrl)) {
      notify.error('Invalid link', 'Links must start with http:// or https://');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        postType,
        visibility,
        text: text.trim(),
        linkUrl: linkUrl.trim() || null,
        clearExistingMedia,
      };
      if (postType === 'photo' && file) payload.file = file;
      if (postType === 'video' && videoFile) payload.video = videoFile;
      if (postType === 'document' && docFile) payload.document = docFile;
      if (postType === 'article') payload.articleTitle = articleTitle.trim();
      if (postType === 'poll') {
        payload.pollOptions = pollOptions.map((o) => o.trim()).filter(Boolean);
        payload.pollDurationDays = pollDays;
      }
      if (scheduledAt) {
        const d = new Date(scheduledAt);
        if (Number.isNaN(d.getTime())) {
          notify.error('Invalid schedule', 'Pick a valid date and time.');
          setSubmitting(false); return;
        }
        if (d.getTime() < Date.now() - 60_000) {
          notify.error('Schedule in the past', 'Pick a time in the future.');
          setSubmitting(false); return;
        }
        payload.scheduledAt = d.toISOString();
      } else if (isRevision && initialDraft?.scheduled_at) {
        payload.clearScheduledAt = true;
      }
      const res = isRevision
        ? await admin.revisePostApproval(target.id, initialDraft.id, payload)
        : await admin.createPostDraftForUser(target.id, payload);
      const id = res?.approval?.id;
      const scheduledBlurb = scheduledAt
        ? `Once the client approves, we'll publish it at ${new Date(scheduledAt).toLocaleString(getIntlLocale())}.`
        : `The client will receive an email and can approve in Approvals → Posts.`;
      notify.success(
        isRevision ? 'Revision sent for approval' : 'Sent for approval',
        `${scheduledBlurb}${id ? `\n\nApproval ID: ${id.slice(0, 12)}…` : ''}`,
      );
      onSent?.();
    } catch (err) {
      notify.error(isRevision ? 'Failed to send revision' : 'Failed to send for approval', err.message || 'Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
      // Backdrop click must NOT discard in-progress drafts — require the
      // explicit X button or Escape to close, matching LinkedIn's behaviour.
      data-testid="admin-composer-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-xl my-8 flex flex-col max-h-[90vh] overflow-hidden shadow-xl"
      >
        {/* Header — actor + visibility */}
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#E3E2E0]">
          <div className="flex items-center gap-3 min-w-0">
            {target.profile_picture ? (
              <img src={target.profile_picture} alt="" className="w-12 h-12 rounded-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#F0E6FF] flex items-center justify-center text-base font-semibold text-[#5A3EA8] shrink-0">
                {(target.name || target.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#191919] truncate">
                Posting as <span className="font-bold">{target.name || target.email}</span>
              </p>
              <button
                type="button"
                onClick={() => setVisOpen((o) => !o)}
                className="mt-0.5 inline-flex items-center gap-1 text-xs text-[#6B6B6B] hover:bg-[#F7F7F5] px-1.5 py-0.5 rounded-md"
                data-testid="composer-visibility-btn"
              >
                {visibility === 'anyone' ? <Globe className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                Post to {visibility === 'anyone' ? 'Anyone' : 'Connections'}
                <ChevronDown className="w-3 h-3" />
              </button>
              {visOpen && (
                <div className="absolute mt-1 w-48 rounded-lg border border-[#E3E2E0] bg-white shadow-lg z-10 py-1">
                  <button
                    onClick={() => { setVisibility('anyone'); setVisOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#191919] hover:bg-[#F7F7F5] text-left"
                    data-testid="composer-vis-anyone"
                  >
                    <Globe className="w-4 h-4" /> Anyone
                  </button>
                  <button
                    onClick={() => { setVisibility('connections'); setVisOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#191919] hover:bg-[#F7F7F5] text-left"
                    data-testid="composer-vis-connections"
                  >
                    <Users className="w-4 h-4" /> Connections only
                  </button>
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F0F0EF] rounded-full" data-testid="composer-close">
            <X className="w-5 h-5 text-[#6B6B6B]" />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {postType === 'article' && (
            <input
              value={articleTitle}
              onChange={(e) => setArticleTitle(e.target.value)}
              placeholder="Article title"
              className="w-full text-2xl font-bold text-[#191919] bg-transparent focus:outline-none placeholder:text-[#9B9B9B] mb-3"
              data-testid="composer-article-title"
            />
          )}

          <textarea
            ref={textRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={postType === 'poll' ? 'Ask a question…' : postType === 'article' ? 'Write your article…' : 'Share your thoughts…'}
            rows={postType === 'article' ? 8 : 6}
            className="w-full text-[15px] text-[#191919] leading-relaxed bg-transparent focus:outline-none resize-none placeholder:text-[#9B9B9B]"
            data-testid="composer-text"
          />

          {/* Image preview */}
          {!filePreview && !clearExistingMedia && postType === 'photo' && initialDraft?.has_image && initialDraft?.media_url && (
            <div className="relative mt-3">
              <img src={initialDraft.media_url} alt="" className="w-full max-h-80 object-cover rounded-lg border border-[#E3E2E0]" />
              <button
                onClick={removeImage}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                data-testid="composer-remove-existing-image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {filePreview && (
            <div className="relative mt-3">
              <img src={filePreview} alt="" className="w-full max-h-80 object-cover rounded-lg border border-[#E3E2E0]" />
              <button
                onClick={removeImage}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                data-testid="composer-remove-image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Video preview */}
          {!videoPreview && !clearExistingMedia && postType === 'video' && initialDraft?.has_video && initialDraft?.media_url && (
            <div className="relative mt-3">
              <video src={initialDraft.media_url} controls className="w-full max-h-80 rounded-lg border border-[#E3E2E0]" />
              <button
                onClick={removeVideo}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                data-testid="composer-remove-existing-video"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {videoPreview && (
            <div className="relative mt-3">
              <video src={videoPreview} controls className="w-full max-h-80 rounded-lg border border-[#E3E2E0]" />
              <button
                onClick={removeVideo}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                data-testid="composer-remove-video"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Document chip */}
          {!docFile && !clearExistingMedia && postType === 'document' && initialDraft?.has_document && (
            <div className="relative mt-3 flex items-center gap-3 border border-[#E3E2E0] bg-[#FBFBFA] rounded-lg p-3">
              <div className="w-10 h-12 rounded-md bg-[#FFEFE0] border border-[#FFD9A8] flex items-center justify-center shrink-0">
                <Paperclip className="w-5 h-5 text-[#9A3412]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#191919] truncate">{initialDraft.document_name || 'Existing document'}</p>
                <p className="text-xs text-[#6B6B6B]">Existing approval media will be kept.</p>
              </div>
              <button
                onClick={removeDoc}
                className="p-1.5 rounded-md hover:bg-[#F0F0EF]"
                data-testid="composer-remove-existing-doc"
              >
                <X className="w-4 h-4 text-[#6B6B6B]" />
              </button>
            </div>
          )}
          {docFile && (
            <div className="relative mt-3 flex items-center gap-3 border border-[#E3E2E0] bg-[#FBFBFA] rounded-lg p-3">
              <div className="w-10 h-12 rounded-md bg-[#FFEFE0] border border-[#FFD9A8] flex items-center justify-center shrink-0">
                <Paperclip className="w-5 h-5 text-[#9A3412]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#191919] truncate">{docFile.name}</p>
                <p className="text-xs text-[#6B6B6B]">{(docFile.size / 1024 / 1024).toFixed(2)} MB · {docFile.type || 'document'}</p>
              </div>
              <button
                onClick={removeDoc}
                className="p-1.5 rounded-md hover:bg-[#F0F0EF]"
                data-testid="composer-remove-doc"
              >
                <X className="w-4 h-4 text-[#6B6B6B]" />
              </button>
            </div>
          )}

          {/* Poll editor */}
          {postType === 'poll' && (
            <div className="mt-4 border border-[#E3E2E0] rounded-lg p-4 bg-[#FBFBFA]">
              <div className="space-y-2">
                {pollOptions.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={opt}
                      onChange={(e) => {
                        const next = [...pollOptions]; next[i] = e.target.value; setPollOptions(next);
                      }}
                      placeholder={`Option ${i + 1}`}
                      maxLength={30}
                      className="flex-1 px-3 py-2 bg-white border border-[#E3E2E0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10"
                      data-testid={`composer-poll-option-${i}`}
                    />
                    {pollOptions.length > 2 && (
                      <button
                        onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}
                        className="p-1.5 text-[#CE3636] hover:bg-[#FFEBE9] rounded-md"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {pollOptions.length < 4 && (
                  <button
                    onClick={() => setPollOptions([...pollOptions, ''])}
                    className="inline-flex items-center gap-1 text-sm text-[#0052CC] hover:bg-[#EAF2FB] px-2 py-1 rounded-md"
                    data-testid="composer-poll-add-option"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add option
                  </button>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-[#6B6B6B]">
                <span>Duration:</span>
                {POLL_DURATIONS.map((d) => (
                  <button
                    key={d.d}
                    onClick={() => setPollDays(d.d)}
                    className={`px-2 py-1 rounded-md ${pollDays === d.d ? 'bg-[#0A0A0A] text-white' : 'bg-white border border-[#E3E2E0] hover:bg-[#F7F7F5]'}`}
                    data-testid={`composer-poll-duration-${d.d}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-[#9B9B9B]">
                LinkedIn poll widgets aren't yet supported by the publishing API — the question and options will be posted as a formatted text card.
              </p>
            </div>
          )}

          {/* Link input */}
          {linkOpen && (
            <div className="mt-3">
              <input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com (LinkedIn will auto-render a preview)"
                className="w-full bg-white border border-[#E3E2E0] rounded-lg text-sm p-2.5 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10"
                data-testid="composer-link-input"
              />
            </div>
          )}

          {/* Schedule input */}
          {scheduleOpen && (
            <div className="mt-3 border border-[#E3E2E0] rounded-lg p-3 bg-[#FBFBFA]">
              <label className="text-xs font-medium text-[#6B6B6B] mb-1.5 flex items-center gap-1">
                <CalendarIcon className="w-3.5 h-3.5" /> Schedule for (your local time)
              </label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-[#E3E2E0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10"
                data-testid="composer-schedule-input"
              />
              <p className="text-[11px] text-[#9B9B9B] mt-1.5">
                The post will go on hold until the client approves it; once approved, it will publish at the scheduled time.
              </p>
              {scheduledAt && (
                <button
                  onClick={() => { setScheduledAt(''); setScheduleOpen(false); }}
                  className="mt-2 text-xs text-[#CE3636] hover:underline"
                  data-testid="composer-schedule-clear"
                >
                  Clear schedule
                </button>
              )}
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="border-t border-[#E3E2E0] px-3 py-2 flex items-center gap-1 flex-wrap">
          <input ref={fileRef} type="file" accept={ALLOWED_IMG.join(',')} className="hidden" onChange={onPickImage} data-testid="composer-image-input" />
          <input ref={videoRef} type="file" accept={ALLOWED_VID.join(',')} className="hidden" onChange={onPickVideo} data-testid="composer-video-input" />
          <input ref={docRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation" className="hidden" onChange={onPickDoc} data-testid="composer-doc-input" />

          <ToolbarBtn icon={Video} label="Video" onClick={() => videoRef.current?.click()} testid="composer-tool-video" color="#1E7F4E" />
          <ToolbarBtn icon={ImageIcon} label="Photo" onClick={() => fileRef.current?.click()} testid="composer-tool-photo" color="#0052CC" />
          <ToolbarBtn icon={Paperclip} label="Document" onClick={() => docRef.current?.click()} testid="composer-tool-document" color="#9A3412" />
          <ToolbarBtn icon={FileText} label="Article" onClick={() => setPostType('article')} active={postType === 'article'} testid="composer-tool-article" color="#9A3412" />
          <ToolbarBtn icon={BarChart2} label="Poll" onClick={() => setPostType('poll')} active={postType === 'poll'} testid="composer-tool-poll" color="#5A3EA8" />

          <div className="ml-auto flex items-center gap-1">
            <div className="relative">
              <button onClick={() => setEmojiOpen((o) => !o)} className="p-2 hover:bg-[#F0F0EF] rounded-md" title="Emoji" data-testid="composer-emoji">
                <Smile className="w-4 h-4 text-[#6B6B6B]" />
              </button>
              {emojiOpen && (
                <div className="absolute bottom-full right-0 mb-2 w-56 p-2 rounded-xl border border-[#E3E2E0] bg-white shadow-lg z-20 grid grid-cols-6 gap-1">
                  {EMOJIS.map((e) => (
                    <button key={e} onClick={() => insertEmoji(e)} className="text-lg hover:bg-[#F7F7F5] rounded p-1">{e}</button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setLinkOpen((o) => !o)}
              className={`px-2 py-1 rounded-md text-xs ${linkOpen ? 'bg-[#F0F0EF] text-[#191919]' : 'text-[#6B6B6B] hover:bg-[#F7F7F5]'}`}
              data-testid="composer-toggle-link"
            >
              + Link
            </button>

            <button
              onClick={handleRewrite}
              disabled={!text.trim() || rewriting}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-[#EADDFF] text-[#5A3EA8] hover:bg-[#DEC9FA] disabled:opacity-50"
              title="Rewrite with AI"
              data-testid="composer-rewrite"
            >
              {rewriting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Rewrite with AI
            </button>

            <span className="text-[11px] text-[#6B6B6B] ml-2">{charCount}/3000</span>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-[#E3E2E0] px-5 py-3 flex items-center justify-between gap-3 bg-[#FBFBFA]">
          <button
            onClick={() => setScheduleOpen((o) => !o)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border ${scheduledAt ? 'border-[#0052CC] text-[#0052CC] bg-[#EAF2FB]' : 'border-[#E3E2E0] text-[#6B6B6B] hover:bg-[#F0F0EF]'}`}
            data-testid="composer-toggle-schedule"
          >
            <CalendarIcon className="w-4 h-4" />
            {scheduledAt ? new Date(scheduledAt).toLocaleString(getIntlLocale()) : 'Schedule'}
          </button>

          <button
            onClick={submit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#0A0A0A] text-white text-sm font-semibold rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-black"
            data-testid="composer-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {scheduledAt ? 'Schedule for approval' : 'Send for approval'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ToolbarBtn({ icon: Icon, label, onClick, active, color, testid }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm hover:bg-[#F0F0EF] ${active ? 'bg-[#F0F0EF] font-medium' : 'text-[#6B6B6B]'}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" style={{ color: color || '#6B6B6B' }} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
